import * as yup from "yup";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { useForm, useWatch, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import { Box } from "@mui/material";

import PasswordInput from "../../../shared/components/password-input";
import * as api from "../../../shared/lib/api";
import { getAddressByCep } from "../../../shared/services/cep";
import { extractApiErrorMessage } from "../../../shared/utils/extract-api-error-message";
import { extractCreatedUserId } from "../../../features/user/utils/extract-created-user-id";
import { isValidCPF } from "../../../shared/utils/is-valid-cpf";
import {
	MaskCEP,
	MaskCNPJ,
	MaskCPF,
	MaskPhone,
} from "../../../shared/utils/mask";
import { onlyAlphanumeric } from "../../../shared/utils/only-alphanumeric";
import { onlyDigits } from "../../../shared/utils/only-digits";

const UF_OPTIONS = [
	"AC",
	"AL",
	"AP",
	"AM",
	"BA",
	"CE",
	"DF",
	"ES",
	"GO",
	"MA",
	"MT",
	"MS",
	"MG",
	"PA",
	"PB",
	"PR",
	"PE",
	"PI",
	"RJ",
	"RN",
	"RS",
	"RO",
	"RR",
	"SC",
	"SP",
	"SE",
	"TO",
];

const BUSINESS_AREA_OPTIONS = [
	{ id: "6", label: "Consumidor Final" },
	{ id: "1", label: "Assistência / Guinchos" },
	{ id: "2", label: "Associações" },
	{ id: "3", label: "Bloqueadores" },
	{ id: "4", label: "Centro Automotivo" },
	{ id: "5", label: "Concessionárias" },
	{ id: "7", label: "Corretora" },
	{ id: "8", label: "Dealers" },
	{ id: "9", label: "Despachantes" },
	{ id: "10", label: "ECV" },
	{ id: "11", label: "Empresa de Vistoria" },
	{ id: "12", label: "Feirões" },
	{ id: "13", label: "Financeiras" },
	{ id: "14", label: "Franquia Dekra" },
	{ id: "15", label: "Frotistas" },
	{ id: "16", label: "Garantia Estendida" },
	{ id: "17", label: "Inspeção Veicular" },
	{ id: "18", label: "Leiloeiro" },
	{ id: "19", label: "Locadora" },
	{ id: "20", label: "Lojista" },
	{ id: "21", label: "Mídia" },
	{ id: "22", label: "Montadora" },
	{ id: "23", label: "Oficinas" },
	{ id: "24", label: "Orgão Público" },
	{ id: "25", label: "Rastreadores" },
	{ id: "26", label: "Rede Postos de Gasolina" },
	{ id: "27", label: "Reguladora de Sinistro" },
	{ id: "28", label: "Seguradora" },
	{ id: "29", label: "Sindicatos" },
	{ id: "30", label: "Sistemas Orçamentos - Reparos" },
	{ id: "31", label: "Sites Classificados" },
	{ id: "32", label: "Software House" },
	{ id: "33", label: "Teste" },
];

const isCreating = (isUpdate: boolean) => !isUpdate;

const validationSchema = yup.object({
	person_type: yup
		.string()
		.oneOf(["pf", "pj"])
		.when("$isUpdate", {
			is: false,
			then: (schema) => schema.required(),
		}),
	first_name: yup.string().when("$isUpdate", {
		is: false,
		then: (schema) => schema.required("Informe o nome"),
	}),
	last_name: yup.string().when("$isUpdate", {
		is: false,
		then: (schema) => schema.required("Informe o sobrenome"),
	}),
	corporate_name: yup.string().when(["person_type", "$isUpdate"], {
		is: (person_type: string, isUpdate: boolean) =>
			person_type === "pj" && isCreating(isUpdate),
		then: (schema) => schema.required("Informe a razão social"),
	}),
	fantasy_name: yup.string().when(["person_type", "$isUpdate"], {
		is: (person_type: string, isUpdate: boolean) =>
			person_type === "pj" && isCreating(isUpdate),
		then: (schema) => schema.required("Informe o nome fantasia"),
	}),
	cnpj: yup.string().when(["person_type", "$isUpdate"], {
		is: (person_type: string, isUpdate: boolean) =>
			person_type === "pj" && isCreating(isUpdate),
		then: (schema) =>
			schema
				.matches(
					/^[A-Za-z0-9]{2}\.[A-Za-z0-9]{3}\.[A-Za-z0-9]{3}\/[A-Za-z0-9]{4}-\d{2}$/,
					{ message: "CNPJ inválido", excludeEmptyString: true },
				)
				.required("Informe o CNPJ"),
	}),
	business_area: yup.string().when(["person_type", "$isUpdate"], {
		is: (person_type: string, isUpdate: boolean) =>
			person_type === "pj" && isCreating(isUpdate),
		then: (schema) => schema.required("Selecione a área de atuação"),
	}),
	state_registration: yup.string(),
	municipal_registration: yup.string(),
	cpf: yup.string().when(["person_type", "$isUpdate"], {
		is: (person_type: string, isUpdate: boolean) =>
			person_type === "pf" && isCreating(isUpdate),
		then: (schema) =>
			schema
				.matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, {
					message: "CPF inválido",
					excludeEmptyString: true,
				})
				.test(
					"cpf-valido",
					"Informe um CPF válido",
					(value) => !value || isValidCPF(value),
				)
				.required("Informe o CPF"),
	}),
	cep: yup
		.string()
		.test(
			"cep-valido",
			"CEP inválido",
			(value) => !value || onlyDigits(value).length === 8,
		)
		.required("Informe o CEP"),
	street: yup.string().required("Informe o endereço"),
	number_address: yup.string().required("Informe o número"),
	complement: yup.string(),
	city: yup.string().required("Informe a cidade"),
	district: yup.string().required("Informe o bairro"),
	uf: yup.string().required("Selecione o estado"),
	phone: yup
		.string()
		.test(
			"telefone-valido",
			"Telefone inválido",
			(value) => !value || onlyDigits(value).length === 11,
		)
		.required("Informe o telefone"),
	cellphone: yup
		.string()
		.test(
			"celular-valido",
			"Celular inválido",
			(value) => !value || onlyDigits(value).length === 11,
		),
	email: yup.string().when("$isUpdate", {
		is: false,
		then: (schema) =>
			schema.email("Informe um email válido").required("Informe o email"),
	}),
	password: yup.string().when("$isUpdate", {
		is: false,
		then: (schema) =>
			schema
				.required("Informe a senha")
				.min(8, "A senha deve conter entre 8 e 16 caracteres")
				.max(16, "A senha deve conter entre 8 e 16 caracteres")
				.matches(/[A-Z]/, "A senha deve conter ao menos uma letra maiúscula")
				.matches(/[a-z]/, "A senha deve conter ao menos uma letra minúscula")
				.matches(/[0-9]/, "A senha deve conter ao menos um número")
				.matches(
					/[_@!()&+$]/,
					"A senha deve conter ao menos um dos seguintes símbolos: _, @, !, (, ), &, +, $",
				),
	}),
	confirm_password: yup.string().when("$isUpdate", {
		is: false,
		then: (schema) =>
			schema
				.oneOf([yup.ref("password")], "As senhas não coincidem")
				.required("Confirme a senha"),
	}),
	share_data: yup.boolean(),
});

type FormValues = yup.InferType<typeof validationSchema>;

export type UserWebProps = Partial<{
	id: string;
	person_type: "pf" | "pj";
	first_name: string;
	last_name: string;
	corporate_name: string;
	fantasy_name: string;
	cnpj: string;
	business_area: string;
	state_registration: string;
	municipal_registration: string;
	cpf: string;
	cep: string;
	street: string;
	number_address: string;
	complement: string;
	city: string;
	district: string;
	uf: string;
	phone: string;
	cellphone: string;
	email: string;
	share_data: boolean;
}>;

interface Props {
	user?: UserWebProps;
	onCreated?: (id: string) => void;
}

function buildDefaultValues(user?: UserWebProps): FormValues {
	return {
		person_type: user?.person_type ?? "pf",
		first_name: user?.first_name ?? "",
		last_name: user?.last_name ?? "",
		corporate_name: user?.corporate_name ?? "",
		fantasy_name: user?.fantasy_name ?? "",
		cnpj: user?.cnpj ?? "",
		business_area: user?.business_area ?? "",
		state_registration: user?.state_registration ?? "",
		municipal_registration: user?.municipal_registration ?? "",
		cpf: user?.cpf ?? "",
		cep: user?.cep ?? "",
		street: user?.street ?? "",
		number_address: user?.number_address ?? "",
		complement: user?.complement ?? "",
		city: user?.city ?? "",
		district: user?.district ?? "",
		uf: user?.uf ?? "",
		phone: user?.phone ?? "",
		cellphone: user?.cellphone ?? "",
		email: user?.email ?? "",
		password: "",
		confirm_password: "",
		share_data: user?.share_data ?? true,
	};
}

export default function CreateOrUpdateUserWebForm({ user, onCreated }: Props) {
	const isUpdate = Boolean(user);

	const {
		control,
		handleSubmit,
		register,
		reset,
		trigger,
		resetField,
		formState: { errors, isSubmitting },
	} = useForm<FormValues>({
		defaultValues: buildDefaultValues(user),
		resolver: yupResolver(validationSchema),
		context: { isUpdate },
	});

	useEffect(() => {
		reset(buildDefaultValues(user));
	}, [user, reset]);

	const personType = useWatch({ control, name: "person_type" });

	async function onSubmit(values: FormValues) {
		if (isUpdate) {
			const payload = {
				phone: onlyDigits(values.phone),
				cep: onlyDigits(values.cep),
				street: values.street,
				number_address: values.number_address,
				complement: values.complement,
				district: values.district,
				city: values.city,
				uf: values.uf,
			};

			const res = (await api.put(`user/update/${user?.id}/`, payload)) as {
				status: number;
				data?: unknown;
			};

			if (res.status === 200) {
				toast.success("Dados atualizados com sucesso!", {
					position: toast.POSITION.TOP_RIGHT,
				});
			} else {
				toast.error(
					extractApiErrorMessage(res) ??
						"Ops.. Tivemos um problema, por favor tente novamente mais tarde!",
					{
						position: toast.POSITION.TOP_RIGHT,
					},
				);
			}
			return;
		}

		const basePayload = {
			person_type: values.person_type,
			email: values.email,
			password: values.password,
			cep: onlyDigits(values.cep),
			street: values.street,
			number_address: values.number_address,
			complement: values.complement,
			district: values.district,
			city: values.city,
			uf: values.uf,
			phone: onlyDigits(values.phone),
		};

		const personPayload =
			values.person_type === "pj"
				? {
						corporate_name: values.corporate_name,
						fantasy_name: values.fantasy_name,
						cnpj: onlyAlphanumeric(values.cnpj ?? ""),
						business_area: values.business_area,
						state_registration: values.state_registration,
						municipal_registration: values.municipal_registration,
					}
				: {
						cpf: onlyDigits(values.cpf ?? ""),
					};

		const payload = {
			...basePayload,
			first_name: values.first_name,
			last_name: values.last_name,
			...personPayload,
		};

		const res = (await api.post("user/signup/", payload)) as {
			status: number;
			data?: unknown;
		};

		if (res.status === 201) {
			toast.success("Usuário cadastrado com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});

			const createdId = extractCreatedUserId(res);

			if (createdId && onCreated) {
				onCreated(createdId);
			} else {
				reset();
			}
		} else {
			toast.error(extractApiErrorMessage(res), {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	async function validateCEP(cep: string) {
		if (onlyDigits(cep).length !== 8) return;

		try {
			const address = await getAddressByCep(cep);
			resetField("street", { defaultValue: address.logradouro });
			resetField("district", { defaultValue: address.bairro });
			resetField("city", { defaultValue: address.localidade });
			resetField("uf", { defaultValue: address.uf });
		} catch {
			toast.error("CEP informado inválido!", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	const isPfCreate = !isUpdate && personType !== "pj";

	const cepField = (
		<>
			CEP*
			<Controller
				name="cep"
				control={control}
				render={({ field }) => (
					<TextField
						fullWidth
						required
						id="cep"
						autoComplete="postal-code"
						placeholder="00000-000"
						error={Boolean(errors.cep)}
						helperText={errors.cep?.message ?? " "}
						InputProps={{ inputComponent: MaskCEP as any }}
						{...field}
						onBlur={(e) => {
							field.onBlur();
							validateCEP((e.target as HTMLInputElement).value);
						}}
					/>
				)}
			/>
		</>
	);

	return (
		<form onSubmit={handleSubmit(onSubmit)} noValidate>
			<Grid container spacing={2}>
				<Grid item md={12} xs={12}>
					<Typography variant="h6">Dados pessoais</Typography>
				</Grid>

				{!isUpdate && (
					<Grid item md={12} xs={12}>
						<Controller
							name="person_type"
							control={control}
							render={({ field }) => (
								<RadioGroup row {...field}>
									<FormControlLabel
										value="pf"
										control={<Radio />}
										label="Pessoa Física"
									/>
									<FormControlLabel
										value="pj"
										control={<Radio />}
										label="Pessoa Jurídica"
									/>
								</RadioGroup>
							)}
						/>
					</Grid>
				)}

				<Grid item md={12} xs={12}>
					<Typography variant="body2" color="text.secondary">
						Os campos marcados com * são obrigatórios
					</Typography>
				</Grid>

				{!isUpdate && (
					<>
						<Grid item md={6} xs={12}>
							Nome*
							<TextField
								fullWidth
								required
								id="first_name"
								autoComplete="given-name"
								placeholder="Informe o nome"
								error={Boolean(errors.first_name)}
								helperText={errors.first_name?.message ?? " "}
								{...register("first_name")}
							/>
						</Grid>
						<Grid item md={6} xs={12}>
							Sobrenome*
							<TextField
								fullWidth
								required
								id="last_name"
								autoComplete="family-name"
								placeholder="Informe o sobrenome"
								error={Boolean(errors.last_name)}
								helperText={errors.last_name?.message ?? " "}
								{...register("last_name")}
							/>
						</Grid>

						{personType === "pj" ? (
							<>
								<Grid item md={6} xs={12}>
									Razão Social*
									<TextField
										fullWidth
										required
										id="corporate_name"
										autoComplete="organization"
										placeholder="Informe a razão social"
										error={Boolean(errors.corporate_name)}
										helperText={errors.corporate_name?.message ?? " "}
										{...register("corporate_name")}
									/>
								</Grid>
								<Grid item md={6} xs={12}>
									Nome Fantasia*
									<TextField
										fullWidth
										required
										id="fantasy_name"
										autoComplete="organization"
										placeholder="Informe o nome fantasia"
										error={Boolean(errors.fantasy_name)}
										helperText={errors.fantasy_name?.message ?? " "}
										{...register("fantasy_name")}
									/>
								</Grid>
								<Grid item md={6} xs={12}>
									CNPJ*
									<Controller
										name="cnpj"
										control={control}
										render={({ field }) => (
											<TextField
												fullWidth
												required
												id="cnpj"
												placeholder="00.000.000/0000-00"
												error={Boolean(errors.cnpj)}
												helperText={errors.cnpj?.message ?? " "}
												InputProps={{ inputComponent: MaskCNPJ as any }}
												{...field}
											/>
										)}
									/>
								</Grid>

								<Grid item md={6} xs={12}>
									Área de atuação*
									<FormControl
										fullWidth
										required
										error={Boolean(errors.business_area)}
									>
										<Controller
											name="business_area"
											control={control}
											render={({ field }) => (
												<Select {...field} id="business_area" displayEmpty>
													<MenuItem value="">
														<em>Selecione</em>
													</MenuItem>
													{BUSINESS_AREA_OPTIONS.map((option) => (
														<MenuItem
															key={`business-area-${option.id}`}
															value={option.id}
														>
															{option.label}
														</MenuItem>
													))}
												</Select>
											)}
										/>
										<FormHelperText>
											{errors.business_area?.message ?? " "}
										</FormHelperText>
									</FormControl>
								</Grid>
								<Grid item md={6} xs={12}>
									Inscrição Estadual
									<TextField
										fullWidth
										id="state_registration"
										placeholder="Informe a inscrição estadual"
										error={Boolean(errors.state_registration)}
										helperText={errors.state_registration?.message ?? " "}
										{...register("state_registration")}
									/>
								</Grid>
								<Grid item md={6} xs={12}>
									Inscrição Municipal
									<TextField
										fullWidth
										id="municipal_registration"
										placeholder="Informe a inscrição municipal"
										error={Boolean(errors.municipal_registration)}
										helperText={errors.municipal_registration?.message ?? " "}
										{...register("municipal_registration")}
									/>
								</Grid>
							</>
						) : (
							<>
								<Grid item md={8} xs={12}>
									CPF*
									<Controller
										name="cpf"
										control={control}
										render={({ field }) => (
											<TextField
												fullWidth
												required
												id="cpf"
												placeholder="000.000.000-00"
												error={Boolean(errors.cpf)}
												helperText={errors.cpf?.message ?? " "}
												InputProps={{ inputComponent: MaskCPF as any }}
												{...field}
												onBlur={() => {
													field.onBlur();
													trigger("cpf");
												}}
											/>
										)}
									/>
								</Grid>
								<Grid item md={4} xs={12}>
									{cepField}
								</Grid>
							</>
						)}
					</>
				)}

				{!isPfCreate && (
					<Grid item md={4} xs={12}>
						{cepField}
					</Grid>
				)}
				<Grid item md={isPfCreate ? 12 : 8} xs={12}>
					Endereço*
					<TextField
						fullWidth
						required
						id="street"
						autoComplete="address-line1"
						placeholder="Informe o endereço"
						error={Boolean(errors.street)}
						helperText={errors.street?.message ?? " "}
						{...register("street")}
					/>
				</Grid>

				<Grid item md={3} xs={12}>
					N°*
					<TextField
						fullWidth
						required
						id="number_address"
						inputMode="numeric"
						placeholder="Número"
						error={Boolean(errors.number_address)}
						helperText={errors.number_address?.message ?? " "}
						{...register("number_address")}
					/>
				</Grid>
				<Grid item md={9} xs={12}>
					Complemento (opcional)
					<TextField
						fullWidth
						id="complement"
						autoComplete="address-line2"
						placeholder="Informe o complemento"
						error={Boolean(errors.complement)}
						helperText={errors.complement?.message ?? " "}
						{...register("complement")}
					/>
				</Grid>

				<Grid item md={5} xs={12}>
					Cidade*
					<TextField
						fullWidth
						required
						id="city"
						autoComplete="address-level2"
						placeholder="Informe a cidade"
						error={Boolean(errors.city)}
						helperText={errors.city?.message ?? " "}
						{...register("city")}
					/>
				</Grid>
				<Grid item md={5} xs={12}>
					Bairro*
					<TextField
						fullWidth
						required
						id="district"
						autoComplete="address-level3"
						placeholder="Informe o bairro"
						error={Boolean(errors.district)}
						helperText={errors.district?.message ?? " "}
						{...register("district")}
					/>
				</Grid>
				<Grid item md={2} xs={12}>
					Estado*
					<FormControl fullWidth required error={Boolean(errors.uf)}>
						<Controller
							name="uf"
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									id="uf"
									displayEmpty
									inputProps={{ autoComplete: "address-level1" }}
								>
									<MenuItem value="">
										<em>UF</em>
									</MenuItem>
									{UF_OPTIONS.map((uf) => (
										<MenuItem key={`uf-${uf}`} value={uf}>
											{uf}
										</MenuItem>
									))}
								</Select>
							)}
						/>
						<FormHelperText>{errors.uf?.message ?? " "}</FormHelperText>
					</FormControl>
				</Grid>

				<Grid item md={isUpdate ? 12 : 6} xs={12}>
					Telefone*
					<Controller
						name="phone"
						control={control}
						render={({ field }) => (
							<TextField
								fullWidth
								required
								id="phone"
								type="tel"
								autoComplete="tel"
								placeholder="(00) 00000-0000"
								error={Boolean(errors.phone)}
								helperText={errors.phone?.message ?? " "}
								InputProps={{ inputComponent: MaskPhone as any }}
								{...field}
							/>
						)}
					/>
				</Grid>
				{!isUpdate && (
					<Grid item md={6} xs={12}>
						Celular
						<Controller
							name="cellphone"
							control={control}
							render={({ field }) => (
								<TextField
									fullWidth
									id="cellphone"
									type="tel"
									autoComplete="tel"
									placeholder="(00) 00000-0000"
									error={Boolean(errors.cellphone)}
									helperText={errors.cellphone?.message ?? " "}
									InputProps={{ inputComponent: MaskPhone as any }}
									{...field}
								/>
							)}
						/>
					</Grid>
				)}

				{!isUpdate && (
					<>
						<Grid item md={12} xs={12}>
							<Divider sx={{ marginY: 2 }} />
							<Typography variant="h6">Dados de acesso</Typography>
						</Grid>

						<Grid item md={12} xs={12}>
							Email*
							<TextField
								fullWidth
								required
								id="email"
								type="email"
								autoComplete="email"
								placeholder="Informe um email"
								error={Boolean(errors.email)}
								helperText={errors.email?.message ?? " "}
								{...register("email")}
							/>
						</Grid>

						<Grid item md={6} xs={12}>
							Senha*
							<PasswordInput
								fullWidth
								required
								id="password"
								autoComplete="new-password"
								error={Boolean(errors.password)}
								helperText={
									errors.password?.message ??
									"Sua senha deve conter entre 8 e 16 caracteres, com letras maiúsculas, minúsculas, números e um dos símbolos: _, @, !, (, ), &, +, $"
								}
								{...register("password")}
							/>
						</Grid>
						<Grid item md={6} xs={12}>
							Confirmar senha*
							<PasswordInput
								fullWidth
								required
								id="confirm_password"
								autoComplete="new-password"
								error={Boolean(errors.confirm_password)}
								helperText={errors.confirm_password?.message ?? " "}
								{...register("confirm_password")}
							/>
						</Grid>

						<Grid item md={12} xs={12}>
							<Controller
								name="share_data"
								control={control}
								render={({ field }) => (
									<FormControlLabel
										control={
											<Checkbox
												checked={Boolean(field.value)}
												onChange={field.onChange}
											/>
										}
										label="Aceito compartilhar os dados (Nome, telefone e e-mail) para receber os informativos e ofertas da Checkauto e seus parceiros."
									/>
								)}
							/>
						</Grid>
					</>
				)}

				<Grid item xs={12}>
					<Box display="flex" justifyContent="center">
						<Button
							variant="contained"
							size="large"
							color="success"
							type="submit"
							disabled={isSubmitting}
						>
							{isSubmitting ? (
								<>
									<CircularProgress
										size={16}
										sx={{ mr: 1, color: "inherit" }}
									/>{" "}
									{isUpdate ? "Atualizando..." : "Cadastrando..."}
								</>
							) : isUpdate ? (
								"Atualizar"
							) : (
								"Cadastrar"
							)}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</form>
	);
}
